import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './DataReadinessPage.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// Cross-practice, super_admin only, like Mission Control: one row per practice, one column
// per data stream, each cell the readiness of that stream for that practice. The vocabulary
// (statuses, ingress methods, granularities) and their labels come from the server so the
// page never hardcodes a word the backend might rename; the colours are keyed by status in
// the stylesheet, which is the one place the status set is repeated.
//
// The per-practice view of the same cells is the Source coverage table on each practice's
// Data page (features/data/SourceCoverageTable.js); clicking a cell here opens that page.

const LEGEND_ORDER = ['live', 'stale', 'partial', 'needs_pull', 'blocked', 'not_applicable'];

function formatGeneratedAt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function Cell({ cell, labels, onOpen }) {
  // Every practice carries every stream key, but a missing cell must not take the row down.
  if (!cell) return <td className="dr-td"><span className="dr-na">—</span></td>;

  const statusLabel = (labels.status && labels.status[cell.status]) || cell.status;
  const granularity = (labels.granularity && labels.granularity[cell.granularity])
    || cell.granularity;
  const ingress = (labels.ingress && labels.ingress[cell.ingress]) || cell.ingress;
  const isNa = cell.status === 'not_applicable';

  // The note explains the status; the record count says how much is behind it. Both ride
  // the cell as a tooltip rather than cluttering a fifteen-column grid.
  const title = `${cell.note || ''}${
    cell.record_count !== null && cell.record_count !== undefined
      ? ` · ${Number(cell.record_count).toLocaleString()} records`
      : ''
  }`;

  return (
    <td className="dr-td">
      <button type="button" className="dr-cell" title={title || undefined} onClick={onOpen}>
        <span className={`dr-chip dr-chip-${cell.status}`}>
          {isNa ? 'n/a' : `${statusLabel}${cell.age ? ` · ${cell.age}` : ''}`}
        </span>
        {!isNa && (
          <span className="dr-meta">{granularity} · {ingress}</span>
        )}
      </button>
    </td>
  );
}

function DataReadinessPage({ onOpenPracticeData, embedded }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [onlyNotLive, setOnlyNotLive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/admin/data-readiness`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Could not load data readiness');
      }
      setData(await response.json());
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const practices = useMemo(() => {
    const all = (data && data.practices) || [];
    // The server already sorts by name; sorting again costs nothing and keeps the order
    // stable if the server ever changes its mind.
    const sorted = [...all].sort((a, b) =>
      String(a.practice_name || '').localeCompare(String(b.practice_name || ''))
    );
    return onlyNotLive ? sorted.filter(p => p.all_live !== true) : sorted;
  }, [data, onlyNotLive]);

  if (loading) return <div className="dr-page"><p className="dr-muted">Loading…</p></div>;

  if (error) {
    return (
      <div className="dr-page">
        <div className="dr-error">
          {error}
          <button type="button" className="dr-retry" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  const streams = (data && data.streams) || [];
  const labels = (data && data.labels) || {};
  const total = ((data && data.practices) || []).length;

  return (
    <div className="dr-page">
      <div className="dr-header">
        <div>
          {/* Inside Admin's tab the tab names the page (2026-10-25). */}
          {!embedded && <h1>Data readiness</h1>}
          <p className="dr-muted">
            Generated {formatGeneratedAt(data && data.generated_at)} · {total} {total === 1 ? 'practice' : 'practices'}
          </p>
        </div>
        <label className="dr-filter">
          <input
            type="checkbox"
            checked={onlyNotLive}
            onChange={e => setOnlyNotLive(e.target.checked)}
          />
          Only what is not live
        </label>
      </div>

      <div className="dr-legend">
        {LEGEND_ORDER.map(status => (
          <span key={status} className={`dr-chip dr-chip-${status}`}>
            {(labels.status && labels.status[status]) || status}
          </span>
        ))}
      </div>

      {practices.length === 0 ? (
        <p className="dr-muted">
          {total === 0 ? 'No practices.' : 'Every practice is live.'}
        </p>
      ) : (
        <div className="dr-scroll">
          <table className="dr-table">
            <thead>
              <tr>
                <th className="dr-practice-col">Practice</th>
                {streams.map(s => <th key={s.key}>{s.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {practices.map(p => {
                const systems = [p.pm_system, p.emr_system].filter(Boolean).join(' / ');
                const open = () => onOpenPracticeData && onOpenPracticeData(p.practice_id);
                return (
                  <tr key={p.practice_id} className="dr-row">
                    <td className="dr-practice-col">
                      <span className="dr-practice-name">{p.practice_name}</span>
                      {systems && <span className="dr-systems">{systems}</span>}
                      {p.ingress_summary && <span className="dr-ingress">{p.ingress_summary}</span>}
                    </td>
                    {streams.map(s => (
                      <Cell
                        key={s.key}
                        cell={p.cells && p.cells[s.key]}
                        labels={labels}
                        onOpen={open}
                      />
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DataReadinessPage;
